import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/auth";
import { vibrate } from "@/lib/quantum";

const QUICK = ["I feel you", "Breathe with me", "Stay here with me", "Thank you"];

const Hearts = ({ bursts }) => (
  <div aria-hidden className="pointer-events-none fixed inset-x-0 bottom-28 z-[60] flex justify-center">
    <AnimatePresence>
      {bursts.map((b) => (
        <motion.span key={b.id} className="absolute" initial={{ opacity: 1, y: 0, x: b.x, scale: 0.6 }} animate={{ opacity: 0, y: -260, x: b.x * 1.6, scale: 1.4 }} exit={{ opacity: 0 }} transition={{ duration: 2.2, ease: "easeOut" }}>
          <Heart className="w-8 h-8 fill-[#E8838E] text-[#E8838E]" />
        </motion.span>
      ))}
    </AnimatePresence>
  </div>
);

// Hearts and short notes between partners during a shared session (all kept in the Sacred Box).
export default function PartnerMessages({ code, messages, joined, partner }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [bursts, setBursts] = useState([]);
  const [incoming, setIncoming] = useState(null);
  const seen = useRef(null);
  const ids = useRef(0);
  const burst = () => {
    const base = (ids.current += 3);
    const mine = [base, base + 1, base + 2];
    setBursts((b) => [...b, ...[-30, 0, 30].map((x, k) => ({ id: mine[k], x }))]);
    setTimeout(() => setBursts((b) => b.filter((x) => !mine.includes(x.id))), 2400);
  };
  useEffect(() => {
    if (seen.current === null) {
      seen.current = messages.length;
      return undefined;
    }
    const fresh = messages.slice(seen.current).filter((m) => !m.mine);
    seen.current = messages.length;
    if (!fresh.length) return undefined;
    const last = fresh[fresh.length - 1];
    vibrate([30, 60, 30]);
    if (fresh.some((m) => m.kind === "heart")) burst();
    if (last.kind !== "text") return undefined;
    setIncoming(last);
    const t = setTimeout(() => setIncoming(null), fresh.length > 1 ? 8000 : 6000);
    return () => clearTimeout(t);
  }, [messages]);
  const send = async (kind, value = text) => {
    if (kind === "text" && !value.trim()) return;
    setBusy(true);
    try {
      await api.post(`/quantum/partner/${code}/message`, { kind, text: kind === "text" ? value.trim() : "" });
      if (kind === "heart") burst();
      else setText("");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  if (!joined) return null;
  const recent = messages.filter((m) => m.kind === "text").slice(-4);
  return (
    <section data-testid="qi-partner-messages" className="mt-8 border border-[#E8838E]/25 p-5">
      <Hearts bursts={bursts} />
      <AnimatePresence>
        {incoming && (
          <motion.div data-testid="qi-partner-incoming" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="fixed left-1/2 -translate-x-1/2 top-[max(1.5rem,env(safe-area-inset-top))] z-[70] max-w-[90vw] rounded-2xl border border-[#E8838E]/60 bg-[#1A0B12]/95 backdrop-blur-xl px-5 py-3 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
            <p className="font-tt-mono text-[10px] uppercase tracking-[0.2em] text-[#E8838E]">{incoming.from_name}</p>
            <p className="mt-1 font-display italic text-lg text-[#F2E8DF]">{incoming.text}</p>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="flex items-center justify-between gap-3">
        <p className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">To {partner}</p>
        <button data-testid="qi-partner-heart" onClick={() => send("heart")} disabled={busy} aria-label="Send a heart" className="w-12 h-12 rounded-full border border-[#E8838E]/60 flex items-center justify-center text-[#E8838E] hover:bg-[#E8838E] hover:text-[#0A0706] active:scale-90 transition-[transform,background-color,color]">
          <Heart className="w-5 h-5 fill-current" />
        </button>
      </div>
      {recent.length > 0 && (
        <ul data-testid="qi-partner-thread" className="mt-4 space-y-2">
          {recent.map((m, k) => (
            <li key={`${m.at}-${k}`} className={`max-w-[85%] rounded-2xl px-4 py-2 text-[15px] ${m.mine ? "ml-auto bg-[#D9B47A]/15 text-[#F2E8DF]" : "bg-[#E8838E]/15 text-[#F2E8DF]"}`}>{m.text}</li>
          ))}
        </ul>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {QUICK.map((q) => <button key={q} data-testid={`qi-partner-quick-${q.split(" ")[0].toLowerCase()}`} onClick={() => send("text", q)} disabled={busy} className="rounded-full border border-[#D9B47A]/25 px-3 py-1.5 text-[13px] text-[#CFC2B6] hover:border-[#D9B47A] transition-colors">{q}</button>)}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send("text"); }} className="mt-3 flex gap-2">
        <input data-testid="qi-partner-text" value={text} onChange={(e) => setText(e.target.value)} maxLength={140} placeholder="A few words…" className="flex-1 min-w-0 bg-[#0F070B] border border-[#D9B47A]/25 px-4 py-3 text-[16px] text-[#F2E8DF] focus:outline-none focus:border-[#E8838E]" />
        <button data-testid="qi-partner-send" disabled={busy || !text.trim()} aria-label="Send" className="w-12 shrink-0 flex items-center justify-center border border-[#D9B47A]/40 text-[#D9B47A] disabled:opacity-40"><Send className="w-4 h-4" /></button>
      </form>
      <p className="mt-3 text-[12px] text-[#A8998C]">Every heart and note is kept in your Sacred Box.</p>
    </section>
  );
}
